import { useMemo, useState } from "react";
import { Button } from "@hybrid-iptv/ui";
import { useActiveProviderSourceId } from "../epg/api.js";
import { useChannels } from "../epg/hooks.js";
import { useCatalogCategoryCounts } from "../library/useCatalogQuery.js";
import { looksAdultCategory, useParental } from "../settings/parentalStore.js";

const PIN_LENGTH = 4;
const UNLOCK_MS = 15 * 60 * 1000;

/**
 * On-screen numeric pad.
 *
 * A password input would summon a soft keyboard on a TV, which is the worst
 * possible D-pad experience. A grid of real buttons is arrow-navigable for
 * free and behaves identically for remote, mouse and touch.
 */
function PinPad({
  value,
  onChange,
}: {
  value: string;
  onChange: (update: (previous: string) => string) => void;
}) {
  // Functional updates, not `onChange(value + digit)`: `value` comes from the
  // render closure, so several presses landing in one tick would all read the
  // same stale string and every digit but the last would be lost. Easy to hit
  // by holding down a remote key.
  const press = (digit: string) => {
    onChange((previous) => (previous.length < PIN_LENGTH ? previous + digit : previous));
  };
  return (
    <div className="pinpad">
      <div
        className="pinpad__display"
        aria-live="polite"
        aria-label={`${value.length} of ${PIN_LENGTH} digits entered`}
      >
        {Array.from({ length: PIN_LENGTH }, (_, i) => (
          <span key={i} className={`pinpad__dot ${i < value.length ? "pinpad__dot--filled" : ""}`} />
        ))}
      </div>
      <div className="pinpad__grid">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} type="button" className="pinpad__key" onClick={() => press(d)} aria-label={`Digit ${d}`}>
            {d}
          </button>
        ))}
        <button type="button" className="pinpad__key" onClick={() => onChange(() => "")} aria-label="Clear">
          Clear
        </button>
        <button type="button" className="pinpad__key" onClick={() => press("0")} aria-label="Digit 0">
          0
        </button>
        <button type="button" className="pinpad__key" onClick={() => onChange((previous) => previous.slice(0, -1))} aria-label="Delete">
          Back
        </button>
      </div>
    </div>
  );
}

export function ParentalPage() {
  const sourceId = useActiveProviderSourceId();
  const {
    pinHash,
    autoLockAdult,
    lockedCategoryIds,
    unlockedUntil,
    setPin,
    clearPin,
    verifyPin,
    setAutoLockAdult,
    toggleCategory,
    unlockFor,
    relock,
  } = useParental();

  // ignoreParental: this picker must show the categories it has itself hidden,
  // or a parent could never un-hide one.
  const channels = useChannels(sourceId, true);
  // Movies and series arrive as category counts (stage B): on the device
  // native SQLite counts them, so this page never loads the whole catalog.
  const movieCounts = useCatalogCategoryCounts(sourceId, "vod");
  const seriesCounts = useCatalogCategoryCounts(sourceId, "series");

  const [entry, setEntry] = useState("");
  const [confirmEntry, setConfirmEntry] = useState("");
  const [mode, setMode] = useState<"idle" | "setting" | "unlocking" | "disabling">("idle");
  const [note, setNote] = useState<string | null>(null);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    const bump = (id: string | undefined, by = 1) => {
      if (!id) return;
      counts.set(id, (counts.get(id) ?? 0) + by);
    };
    for (const c of channels.data?.channels ?? []) bump(c.categoryId);
    for (const [id, n] of movieCounts.data ?? []) bump(id, n);
    for (const [id, n] of seriesCounts.data ?? []) bump(id, n);
    return [...counts.entries()]
      .map(([id, count]) => ({ id, count, auto: looksAdultCategory(id) }))
      .sort((a, b) => b.count - a.count || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  }, [channels.data, movieCounts.data, seriesCounts.data]);

  const isUnlocked = unlockedUntil !== null && unlockedUntil > Date.now();

  const handleSet = async () => {
    if (entry.length !== PIN_LENGTH) {
      setNote(`Enter ${PIN_LENGTH} digits.`);
      return;
    }
    if (entry !== confirmEntry) {
      setConfirmEntry("");
      setNote("The two PINs did not match.");
      return;
    }
    await setPin(entry);
    setEntry("");
    setConfirmEntry("");
    setMode("idle");
    setNote("PIN set. Hidden categories no longer appear anywhere in the app.");
  };

  const handleVerified = async (then: () => void) => {
    if (!(await verifyPin(entry))) {
      setEntry("");
      setNote("Incorrect PIN.");
      return;
    }
    setEntry("");
    setMode("idle");
    setNote(null);
    then();
  };

  return (
    <div className="page page--source">
      <div className="settings-card">
        <div className="settings-header">
          <h1 className="settings-title">Parental Controls</h1>
          <p className="settings-subtitle">
            Hide categories behind a PIN. Hidden categories do not appear anywhere in the app,
            including rails, the guide and search.
          </p>
        </div>

        {note && <p className="settings-save-note">{note}</p>}

        {!pinHash && mode !== "setting" && (
          <div className="settings-actions">
            <Button
              variant="primary"
              onClick={() => {
                setMode("setting");
                setNote(null);
              }}
            >
              Set a PIN
            </Button>
          </div>
        )}

        {mode === "setting" && (
          <>
            <p className="settings-label">Enter a {PIN_LENGTH}-digit PIN</p>
            <PinPad value={entry} onChange={setEntry} />
            <p className="settings-label">Confirm</p>
            <PinPad value={confirmEntry} onChange={setConfirmEntry} />
            <div className="settings-actions">
              <Button variant="primary" onClick={() => void handleSet()}>
                Save PIN
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setMode("idle");
                  setEntry("");
                  setConfirmEntry("");
                  setNote(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </>
        )}

        {pinHash && (mode === "unlocking" || mode === "disabling") && (
          <>
            <p className="settings-label">Enter your PIN</p>
            <PinPad value={entry} onChange={setEntry} />
            <div className="settings-actions">
              <Button
                variant="primary"
                onClick={() =>
                  void handleVerified(() => {
                    if (mode === "disabling") {
                      clearPin();
                      setNote("Parental controls are off.");
                    } else {
                      unlockFor(UNLOCK_MS);
                    }
                  })
                }
              >
                Confirm
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setMode("idle");
                  setEntry("");
                  setNote(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </>
        )}

        {pinHash && mode === "idle" && (
          <div className="settings-actions">
            {isUnlocked ? (
              <Button variant="default" onClick={relock}>
                Hide again now
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={() => {
                  setMode("unlocking");
                  setNote(null);
                }}
              >
                Show hidden for 15 minutes
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => {
                setMode("disabling");
                setNote("Enter your PIN to turn parental controls off.");
              }}
            >
              Turn off
            </Button>
          </div>
        )}

        {pinHash && (
          <>
            <div className="settings-row">
              <label className="settings-row-label" htmlFor="auto-adult">
                Auto-hide adult categories
              </label>
              <input
                id="auto-adult"
                type="checkbox"
                checked={autoLockAdult}
                onChange={(e) => setAutoLockAdult(e.target.checked)}
                aria-label="Automatically hide categories that look adult"
              />
            </div>

            <p className="settings-save-note">
              Detected by category name. Tick anything else to hide it — the list is your own
              source categories, so it still works when a name gives nothing away.
            </p>

            <div className="provider-list">
              {categories.length === 0 && (
                <p className="settings-save-note">No categories loaded yet.</p>
              )}
              {categories.map((c) => {
                const manual = lockedCategoryIds.includes(c.id);
                const hiddenByAuto = autoLockAdult && c.auto && !manual;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`provider-item ${manual || hiddenByAuto ? "provider-item--active" : ""}`}
                    onClick={() => toggleCategory(c.id)}
                    aria-pressed={manual || hiddenByAuto}
                  >
                    <span className="provider-item__name">{c.id}</span>
                    <span className="provider-item__status">
                      {c.count} · {manual ? "Hidden" : hiddenByAuto ? "Hidden (auto)" : "Visible"}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
